/**
 * Songs: the library as a list. The engine filters the whole library (the
 * search box, the genre chips and the filter panel are one query), the grid
 * only draws the rows on screen, and every row plays on double-click and has a
 * ⋯ menu.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { writeTags } from "../api";
import { editableColumns, editableFor, fetchValues, type EditableColumn } from "../core/edits";
import { openReview, openSetValue } from "../shell/EditDialogs";
import { toast } from "../shell/Toast";
import { useApp } from "../core/app";
import { player, toQueueTrack, usePlayer } from "../core/player";
import { startTrackDrag } from "../core/trackDrag";
import { ColumnPicker, type NewColumn } from "../grid/ColumnPicker";
import { Grid, type RowMenuOpener } from "../grid/Grid";
import {
  BUILTIN_COLUMNS,
  applyLayout,
  moveColumn,
  resetLayout,
  resizeColumn,
  saveLayout,
  sortField,
  toggleColumn,
  type ColumnDef,
} from "../grid/columns";
import { EMPTY_SELECTION, applyClick, clearSelection, pruneSelection, selectAll } from "../grid/selection";
import { evaluate } from "../search/evaluate";
import { FilterPanel } from "../search/FilterPanel";
import { filterClauses, isActive } from "../search/filters";
import { Icon } from "../shell/Icons";
import { MoreButton, PopupMenu, type Anchor } from "../shell/PopupMenu";
import { clockTime } from "../shell/Waveform";
import { BulkMatrix } from "../studio/BulkMatrix";
import type { Track } from "../types";
import { trackMenu } from "./trackMenu";
import { copyInto } from "./BrowseViews";
import { songs } from "../format/count";


export function useColumns() {
  const app = useApp();
  const [columns, setColumns] = useState<ColumnDef[]>(() => applyLayout(BUILTIN_COLUMNS));
  useEffect(() => {
    const custom: ColumnDef[] = app.library.columns.map((c) => ({
      id: c.key, label: c.label, width: 120, minWidth: 60,
      align: c.type === "number" ? "right" : "left", kind: "custom", valueType: c.type,
    }));
    setColumns(applyLayout([...BUILTIN_COLUMNS, ...custom]));
  }, [app.library.columns]);
  useEffect(() => saveLayout(columns), [columns]);
  return [columns, setColumns] as const;
}

/** The selection, kept while you visit the tag editor and come Back. */
let remembered: { ids: string[]; anchor: string | null } | null = null;
/** The column the list is sorted by, kept the same way (the library holds the order itself). */
let rememberedSort: { columnId: string; dir: "asc" | "desc" } | null = null;

export function SongsView({
  title = "Songs",
  inlineEdit = false,
  onFocusChange,
  pickToEdit = false,
}: {
  title?: string;
  inlineEdit?: boolean;
  onFocusChange?: (t: Track | null) => void;
  /** The Tag editor's song picker: double-click opens a song in the editor. */
  pickToEdit?: boolean;
}) {
  const app = useApp();
  const p = usePlayer();
  const lib = app.library;
  const [columns, setColumns] = useColumns();
  const [selection, setSelection] = useState(() =>
    remembered ? { ...EMPTY_SELECTION, ids: new Set(remembered.ids), anchor: remembered.anchor } : EMPTY_SELECTION
  );
  useEffect(() => {
    remembered = { ids: [...selection.ids], anchor: selection.anchor ?? null };
  }, [selection]);
  const editSongs = (ts: Track[]) => ts.length && app.navigate("studio", { ids: ts.map((t) => t.id).join(",") });
  const [dragging, setDragging] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ tracks: Track[]; anchor: Anchor; columnId?: string } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [matrixOpen, setMatrixOpen] = useState(false);
  const scroll = useRef<HTMLDivElement | null>(null);
  const [sort, setSort] = useState(rememberedSort);
  const [dropFiles, setDropFiles] = useState(false);
  /** A header click: ascending, then descending, then back to the library's own order. */
  const sortBy = (columnId: string) => {
    const col = columns.find((c) => c.id === columnId);
    const field = col ? sortField(col) : null;
    if (!field) return;
    const next = sort?.columnId !== columnId ? { columnId, dir: "asc" as const }
      : sort.dir === "asc" ? { columnId, dir: "desc" as const } : null;
    rememberedSort = next;
    setSort(next);
    scroll.current?.scrollTo({ top: 0 });
    void lib.setSort(next ? { field, dir: next.dir } : null);
  };

  // Offline: the sample data is evaluated here with the same parser and AST.
  const local = useMemo(() => (app.offline ? evaluate(lib.tracks, app.query) : null), [app.offline, lib.tracks, app.query]);
  const matched = local ? local.matched : lib.tracks;
  // Every loaded row; the grid asks for more as they scroll near (no "show more").
  const shown = matched;
  const visibleIds = useMemo(() => shown.map((t) => t.id), [shown]);
  useEffect(() => setSelection((s) => pruneSelection(s, new Set(visibleIds))), [visibleIds]);

  const loadMore = lib.loadMore;
  const needRows = useCallback((upTo: number) => {
    if (!local) void loadMore(upTo);
  }, [local, loadMore]);

  const focused = matched.find((t) => t.id === app.focused) ?? null;
  // Coming Back to the list: put the song you were on in view again.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || !shown.length || !scroll.current) return;
    restored.current = true;
    const i = shown.findIndex((t) => t.id === app.focused);
    const row = scroll.current.querySelector("tbody tr:not(.spacer)") as HTMLElement | null;
    if (i > 0) scroll.current.scrollTop = Math.max(0, i * (row?.offsetHeight || 34) - scroll.current.clientHeight / 3);
  }, [shown.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => onFocusChange?.(focused), [focused, onFocusChange]);
  const selectedTracks = useMemo(() => shown.filter((t) => selection.ids.has(t.id)), [shown, selection]);
  const loadedSeconds = useMemo(() => matched.reduce((s, t) => s + (t.duration || 0), 0), [matched]);
  // The engine's total for every match, so the header never counts only what has loaded.
  const totalSeconds = !local && lib.totalSeconds != null ? lib.totalSeconds : loadedSeconds;

  const onRowClick = (id: string, mods: { shift: boolean; meta: boolean }) => {
    setSelection((s) => applyClick(s, id, visibleIds, mods));
    if (!mods.shift && !mods.meta) app.setFocused(id);
  };

  const editValue = useCallback(
    async (track: Track, field: string, value: string): Promise<string | null> => {
      const written = await writeTags(track.id, { [field]: value === "" ? null : value }, {});
      return written.error ?? null;
    },
    []
  );

  const openMenu: RowMenuOpener = (track, anchor, columnId) => {
    const tracks = selection.ids.has(track.id) && selection.ids.size > 1 ? selectedTracks : [track];
    setMenu({ tracks, anchor, columnId });
  };

  // Keyboard on the list: Enter plays, Q queues, Delete removes, Ctrl+A selects.
  const onKey = (e: React.KeyboardEvent) => {
    const el = e.target as HTMLElement;
    if (el.closest("input, textarea, [contenteditable]")) return;
    const mod = e.ctrlKey || e.metaKey;
    const current = selectedTracks.length ? selectedTracks : focused ? [focused] : [];
    if (e.key === "Enter" && current.length) {
      e.preventDefault();
      if (mod) player.playNext(current);
      else if (current.length === 1) player.playTracks(matched, matched.findIndex((t) => t.id === current[0].id));
      else player.playTracks(current);
    } else if (e.key.toLowerCase() === "q" && !mod && current.length) {
      e.preventDefault();
      player.enqueue(current);
    } else if (mod && e.key.toLowerCase() === "a") {
      e.preventDefault();
      setSelection(selectAll(visibleIds));
    } else if ((e.key === "ArrowDown" || e.key === "ArrowUp") && shown.length) {
      e.preventDefault();
      const i = shown.findIndex((t) => t.id === app.focused);
      const next = shown[Math.max(0, Math.min(shown.length - 1, (i < 0 ? -1 : i) + (e.key === "ArrowDown" ? 1 : -1)))];
      setSelection(applyClick(clearSelection(), next.id, visibleIds, { shift: false, meta: false }));
      app.setFocused(next.id);
      scroll.current?.querySelector(`tr[data-track-id="${next.id}"]`)?.scrollIntoView({ block: "nearest" });
    } else if (e.key === "Escape" && selection.ids.size) {
      setSelection(clearSelection());
    }
  };

  const genres = useMemo(() => [...lib.genres].sort((a, b) => b.tracks - a.tracks).slice(0, 8), [lib.genres]);
  const activeGenre = app.typedQuery.clauses.find((c) => c.field === "genre" && !c.negated);
  const setGenre = (name: string | null) => {
    const rest = app.input.replace(/(^|\s)genre:("[^"]*"|\S+)/g, "").trim();
    app.setInput(name ? `${rest} genre:"${name}"`.trim() : rest);
  };

  const headerMenu = () => [
    { id: "select", group: "a", label: "Select all", shortcut: "Ctrl+A", run: () => setSelection(selectAll(visibleIds)) },
    { id: "queue", group: "a", label: "Add all to queue", icon: "queue" as const, disabled: !matched.length, run: () => player.enqueue(matched) },
    { id: "filters", group: "b", label: filtersOpen ? "Hide filters" : "Show filters…", icon: "filter" as const, run: () => setFiltersOpen((o) => !o) },
    { id: "columns", group: "b", label: "Columns…", icon: "columns" as const, run: () => setPickerOpen(true) },
    { id: "reset", group: "b", label: "Reset columns", run: () => { resetLayout(); setColumns(applyLayout(BUILTIN_COLUMNS)); } },
    {
      id: "review", group: "c", label: "Review values", icon: "list" as const, disabled: app.offline,
      submenu: editableColumns(lib.columns).map((col) => ({ id: `r-${col.id}`, label: `${col.label}…`, run: () => openReview(col) })),
    },
  ];

  /** Right-click on a column header: review its values, or fill its gaps. */
  const [colMenu, setColMenu] = useState<{ col: EditableColumn; at: { x: number; y: number } } | null>(null);
  const columnMenu = (col: EditableColumn) => [
    { id: "review", label: `Review ${col.label.toLowerCase()} values…`, icon: "list" as const, run: () => openReview(col) },
    {
      id: "empty", label: `Edit songs with no ${col.label.toLowerCase()}…`, icon: "edit" as const,
      run: async () => {
        const res = await fetchValues(col.id);
        const empty = res.values.find((v) => v.value === null);
        if (!empty?.count) toast(`Every song has ${col.label.toLowerCase()} filled in.`, "ok");
        else app.navigate("studio", { ids: empty.trackIds.join(",") });
      },
    },
    ...(selectedTracks.length > 1
      ? [{ id: "set", group: "b", label: `Set ${col.label} for the ${selectedTracks.length} selected…`, icon: "edit" as const, run: () => openSetValue(selectedTracks.map((t) => t.id), col) }]
      : []),
  ];

  const selectionMenu = () => [
    ...trackMenu(app, selectedTracks, matched),
    { id: "matrix", group: "8", label: "Set era, energy, genre…", icon: "grid" as const, disabled: app.offline, run: () => setMatrixOpen(true) },
  ];

  return (
    <section
      className={`nv-view nv-songs ${dropFiles ? "nv-drop-files" : ""}`}
      aria-label={title}
      onKeyDown={onKey}
      // Showing one folder: songs dropped from the computer are copied into it.
      onDragOver={(e) => {
        if (lib.scope.rootId === undefined || !Array.from(e.dataTransfer.types).includes("Files")) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        if (!dropFiles) setDropFiles(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropFiles(false);
      }}
      onDrop={(e) => {
        if (lib.scope.rootId === undefined || !Array.from(e.dataTransfer.types).includes("Files")) return;
        e.preventDefault();
        setDropFiles(false);
        void copyInto(lib.refresh, { rootId: lib.scope.rootId, folder: lib.scope.folder ?? "",
          label: lib.scope.label ?? lib.scope.folder ?? "this folder" }, Array.from(e.dataTransfer.files));
      }}
    >
      <div className="nv-viewhead">
        <div className="nv-viewtitle">
          <h1>{title}</h1>
          <div className="nv-sub">
            {app.query.isEmpty ? songs(lib.total) : local ? `${matched.length.toLocaleString()} of ${lib.total.toLocaleString()} match` : `${songs(lib.total)} match`}
            {matched.length > 0 && ` · ${clockTime(totalSeconds)}`}
            {lib.pendingExcluded > 0 && ` · ${lib.pendingExcluded} still analysing`}
            {pickToEdit && " · Double-click a song to edit it, or select several and press Edit tags"}
            {lib.scope.rootId !== undefined && (
              <span className="nv-scope-chip">
                <Icon name="folder" size={12} /> {lib.scope.label ?? lib.scope.folder ?? "One folder"}
                <button className="nv-iconbtn" title="Show all songs" aria-label="Show all songs" onClick={() => void lib.setScope({})}><Icon name="close" size={12} /></button>
              </span>
            )}
            {lib.scope.rootId !== undefined && (
              <span className="nv-dim"> · drop songs here to copy them into this folder</span>
            )}
          </div>
        </div>
        <div className="nv-actions">
          <button className="nv-btn primary" disabled={!matched.length} onClick={() => player.playTracks(matched)}>
            <Icon name="play" /> Play all
          </button>
          <button className="nv-btn" disabled={!matched.length} onClick={() => { player.setShuffle(true); player.playTracks(matched, Math.floor(Math.random() * matched.length)); }}>
            <Icon name="shuffle" /> Shuffle
          </button>
          <button className={`nv-iconbtn ${filtersOpen || isActive(app.filters) ? "on" : ""}`} title="Filters: several artists, BPM and energy ranges" onClick={() => setFiltersOpen((o) => !o)}>
            <Icon name="filter" />
            {isActive(app.filters) && <span className="nv-badge">{filterClauses(app.filters).length}</span>}
          </button>
          <button className="nv-iconbtn" title="Columns" onClick={() => setPickerOpen(true)}>
            <Icon name="columns" />
          </button>
          <MoreButton label="List options" items={headerMenu} />
        </div>
      </div>

      <div className="nv-chips" role="toolbar" aria-label="Genres">
        <button className={`nv-chip ${!activeGenre ? "on" : ""}`} onClick={() => setGenre(null)}>All</button>
        {genres.map((g) => (
          <button key={g.name} className={`nv-chip ${activeGenre && String(activeGenre.value).toLowerCase() === g.name.toLowerCase() ? "on" : ""}`} onClick={() => setGenre(g.name)} title={songs(g.tracks)}>
            {g.name}
          </button>
        ))}
        {lib.genres.length > genres.length && (
          <button className="nv-chip" onClick={() => app.navigate("genres")}>+ {lib.genres.length - genres.length}</button>
        )}
      </div>

      {filtersOpen && <FilterPanel filters={app.filters} offline={app.offline} onChange={app.setFilters} />}

      {selection.ids.size > 1 && (
        <div className="nv-selbar" role="toolbar" aria-label="Selection">
          <b>{selection.ids.size} selected</b>
          <button className="nv-btn sm" onClick={() => player.playTracks(selectedTracks)}><Icon name="play" /> Play</button>
          <button className="nv-btn sm" onClick={() => player.enqueue(selectedTracks)}><Icon name="queue" /> Add to queue</button>
          <button className={`nv-btn sm ${pickToEdit ? "primary" : ""}`} disabled={app.offline} onClick={() => editSongs(selectedTracks)}><Icon name="edit" /> Edit tags</button>
          <MoreButton label="More for the selection" items={selectionMenu} />
          <span className="nv-spacer" />
          <button className="nv-link" onClick={() => setSelection(clearSelection())}>Clear selection</button>
        </div>
      )}

      <div className="nv-gridwrap" ref={scroll} tabIndex={0} aria-label="Songs list">
        {matched.length === 0 ? (
          <div className="nv-empty">
            <Icon name="search" size={22} />
            {lib.state === "connected" && lib.total === 0 ? (
              <>
                <b>Your library is empty</b>
                <span>Add a music folder from File → Add music folder…</span>
              </>
            ) : (
              <>
                <b>No songs match</b>
                <span>Try fewer words, or <code>genre:amapiano</code>, <code>bpm:110..120</code>, <code>key:8A</code>.</span>
              </>
            )}
          </div>
        ) : (
          <>
            <Grid
              sort={sort}
              onSort={sortBy}
              onHeaderMenu={(id, at) => {
                const col = editableFor(id, lib.columns);
                if (col) setColMenu({ col, at });
              }}
              tracks={shown}
              columns={columns}
              selected={app.focused}
              selectedIds={selection.ids}
              onSelect={onRowClick}
              onEdit={editValue}
              onFilter={app.addTerm}
              onResize={(id, w) => setColumns((c) => resizeColumn(c, id, w))}
              onReorder={(from, to) => setColumns((c) => moveColumn(c, from, to))}
              dragging={dragging}
              onDragState={setDragging}
              scrollRef={scroll}
              rowCount={!local && lib.hasMore ? Math.max(lib.total, matched.length) : matched.length}
              onNeedRows={needRows}
              notation={app.notation}
              playingId={p.current?.track.id ?? null}
              onOpen={(t) => (pickToEdit ? editSongs([t]) : player.playTracks(matched, matched.findIndex((x) => x.id === t.id)))}
              onMenu={openMenu}
              inlineEdit={inlineEdit}
              // Drag songs out (into Radio's Log, say): the selection when the row is in it, else the row.
              onDragRows={inlineEdit ? undefined : (t, e) => {
                const picked = selection.ids.has(t.id) ? matched.filter((x) => selection.ids.has(x.id)) : [t];
                startTrackDrag(e, picked.map(toQueueTrack));
              }}
            />
          </>
        )}
      </div>

      {colMenu && <PopupMenu items={columnMenu(colMenu.col)} anchor={colMenu.at} onClose={() => setColMenu(null)} label={`${colMenu.col.label} column`} />}
      {menu && <PopupMenu items={trackMenu(app, menu.tracks, matched, menu.columnId)} anchor={menu.anchor} onClose={() => setMenu(null)} label="Song options" />}
      {pickerOpen && (
        <ColumnPicker
          columns={columns.filter((c) => c.id !== "more")}
          onToggle={(id) => setColumns((c) => toggleColumn(c, id))}
          onReset={() => { resetLayout(); setColumns(applyLayout(BUILTIN_COLUMNS)); }}
          onCreate={(col: NewColumn) => lib.createColumn(col.label, col.valueType)}
          onDelete={(id) => void lib.deleteColumn(id)}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {matrixOpen && (
        <BulkMatrix tracks={selectedTracks} columns={lib.columns} genres={lib.genres} onCreateColumn={lib.createColumn} onClose={() => setMatrixOpen(false)} onDone={() => void lib.refresh()} />
      )}
    </section>
  );
}
